import { Component, Inject, OnInit, ChangeDetectorRef, ElementRef, ViewChild, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, Observable, Observer } from 'rxjs';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { environment } from 'src/environments/environment';
import { LayoutUtilsService, LoaderService, RequestService } from 'src/app/shared/services';
import { SpinnerService } from 'src/app/shared/services/spinner.service';

interface ViewDialogData {
	title: string;
	data: any;
	clientId: string;
	image: string;
	isUser: boolean;
}

@Component({
	selector: 'app-crop-dialog-modal',
	templateUrl: './custom-crop-dialog.component.html',
	styleUrls: ['./custom-crop-dialog.component.scss'],
	standalone: false
})
export class ModalCropDialogComponent implements OnInit {
	private subscriptions: Subscription[] = [];
	public orgType = environment.orgType;
	public originalImage = undefined;
	public selectedUser: any;
	public currentselectedUser = undefined;
	public errorMessage: string = '';
	public passingThru: boolean = false;
	public loading: boolean = false;
	public loadingDelete: boolean = false;
	public hasFormErrors: boolean = false;
	public dataType: string = 'img/resource';
	public dataTypeDisplay: string = this.translate.instant('Image');
	public dataTypeDisplayFolder: string = this.translate.instant('Folder');
	public dataList: any[] = [];
	public dataListFolder: any[] = [];
	public imageSelected: any = undefined;
	public imageUrlPath: any = undefined;
	public imageKey: number = 0; // Used to force image cropper to reload
	public selectedRatio: number = 1;
	public folderText: string = '';
	public folderSelected: string = '';
	public tabSelected: number = 0;
	public searchVal: string = '';
	public imageChangedEvent: any = '';
	public croppedImage: any = '';
	public imageExtension: string = 'png';
	public allowedExtensions: string[] = ['jpeg', 'jpg', 'png', 'gif'];
	public allowedAcceptExtensions: string = 'image/jpg,image/jpeg,image/png,image/gif';

	// public defaultPictureLink: string = 'assets/vertical/' + this.orgType + '/images/profile.png';
	public defaultPictureLink: string = 'assets/images/profile.png';
	public pictureLink: string = JSON.parse(JSON.stringify(this.defaultPictureLink));
	/** Set when profile image delete API succeeds; parent uses this on Cancel to refresh [src]. */
	public profileImageDeleted = false;

	public paginatorTotal: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	pageSize = 10;
	pageNumber = 1;

	orderDir = 'asc';
	orderBy = '_id'; // uid

	@ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;
	@ViewChild('imageInput') imageInput: ElementRef;

	constructor(private zone: NgZone, private translate: TranslateService,
		private requestService: RequestService,
		private cdr: ChangeDetectorRef, private spinnerService: SpinnerService,
		private layoutUtilsService: LayoutUtilsService,
		public dialogRef: MatDialogRef<ModalCropDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ViewDialogData) {
		// console.log('ModalGalleryDialogComponent', data);
	}

	ngOnInit() {
		this.profileImageDeleted = false;
		if (this.data && this.data.image && this.data.image !== '') {
			const dataUriRegex = /^data:(.*?)(;base64)?,(.*)$/;
			if (dataUriRegex.test(this.data.image)) {
				// It's already a base64 data URI
				this.imageUrlPath = this.data.image;
				this.imageSelected = { error: false, text: '', originalFile: undefined, source_url: null };
				this.originalImage = this.data.image;
				this.pictureLink = this.data.image;
				// Extract image extension from data URI if possible
				const mimeMatch = this.data.image.match(/data:image\/([^;]+)/);
				if (mimeMatch && mimeMatch[1]) {
					this.imageExtension = mimeMatch[1].toLowerCase();
				}
				this.imageKey = 0;
				this.cdr.detectChanges();
			}
			else {
				// It's a URL, need to convert to base64
				this.originalImage = this.data.image;
				this.pictureLink = this.data.image;
				this.cdr.detectChanges();
				this.imageSelected = { error: false, text: this.getFilename(this.data.image), originalFile: undefined, source_url: null };
				// Try to detect image extension from URL
				const urlExtension = this.data.image.split('.').pop()?.split('?')[0]?.toLowerCase();
				if (urlExtension && this.allowedExtensions.includes(urlExtension)) {
					this.imageExtension = urlExtension;
				}
				this.getBase64ImageFromFile(this.pictureLink);
				this.cdr.detectChanges();
			}
		}
		this.subscriptions.push(
			this.requestService.currentUserSubject.subscribe((data) => {
				if (data) {
					// Defer updates to avoid ExpressionChangedAfterItHasBeenCheckedError
					queueMicrotask(() => {
						// this.selectedUser = data;
						this.currentselectedUser = data;
						// console.log('currentUserSubject', this.currentselectedUser)
						// if (this.currentselectedUser.pictureLink && this.currentselectedUser.pictureLink !== '') {
						// 	this.originalImage = this.currentselectedUser.pictureLink;
						// 	this.pictureLink = this.currentselectedUser.pictureLink;

						// 	this.getBase64ImageFromFile(this.pictureLink);
						// 	this.imageSelected = { error: false, text: this.getFilename(this.currentselectedUser.pictureLink), originalFile: undefined, source_url: null }
						// }
						this.cdr.detectChanges();
						this.cdr.markForCheck();
					});
				}
			})
		);
	}
	public rationChanged(event) {
		this.selectedRatio = Number(event.value);
	}
	/**
	 * On Destroy
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(el => el.unsubscribe());
	}
	public getBase64ImageFromFile(imageUrl) {
		this.spinnerService.show();
		this.loading = true;
		this.cdr.detectChanges();

		this.getBase64ImageFromURL(imageUrl).subscribe(
			base64data => {
				// this is the image as dataUrl
				const dataUrl = 'data:image/' + this.imageExtension + ';base64,' + base64data;
				// Force cropper to reload by clearing and incrementing key
				this.imageUrlPath = undefined;
				this.imageKey = -1; // Hide cropper temporarily
				this.cdr.detectChanges();
				// Use setTimeout to ensure DOM updates before showing new image
				setTimeout(() => {
					this.imageKey++;
					this.imageUrlPath = dataUrl;
					this.pictureLink = this.imageUrlPath;
					this.spinnerService.hide();
					this.loading = false;
					this.cdr.detectChanges();
				}, 10);
			},
			err => {
				this.loading = false;
				this.spinnerService.hide();
				this.layoutUtilsService.showNotification(
					this.translate.instant('Error: ') + ' ' + this.translate.instant('The image is not accessible'),
					this.translate.instant('Dismiss')
				);
				this.cdr.detectChanges();
			}
		);
	}
	closeModal(data): void {
		this.zone.run(() => {
			this.dialogRef.close(data);
		});
	}
	goToCrop(): void {
		this.zone.run(() => {
			// console.log(this.imageUrlPath);
			this.tabSelected = 1;
		});
	}
	getFilename(url) {
		if (url) {
			var m = url.toString().match(/.*\/(.+?)\./);
			if (m && m.length > 1) {
				return m[1];
			}
		}
		return "";
	}
	onBrowseFiles(target: any): void {
		this.readFiles(target.files);
	}
	/**
	 *  @param files: list of browsed files
	 *  @param index: iterator over browsed images
	 *
	 *  read files browsed by user
	 */
	readFiles(files, index = 0): void {
		let reader = new FileReader();
		if (index in files) {
			let currentFile = { error: false, text: files[index].name.split('.')[0], id: files[index].id, originalFile: files[index], source_url: null, extension: '' };
			let fileExt = files[index].name.split('.').pop();
			currentFile.extension = fileExt.toLowerCase();
			this.imageExtension = fileExt.toLowerCase();
			const max_size = 5000000;
			const max_height = 300;
			const max_width = 300;
			if (files[index].size > max_size) {
				this.layoutUtilsService.showNotification(this.translate.instant('Maximum size allowed is') + ' ' + max_size / 1000000 + 'MB', this.translate.instant('Dismiss'));
			} else if (this.allowedExtensions.indexOf(fileExt.toLowerCase()) === -1) {
				currentFile.error = true;
				this.layoutUtilsService.showNotification(this.translate.instant('The file type is not allowed'), this.translate.instant('Dismiss'));
			} else {
				this.loading = true;
				this.spinnerService.show();
				this.cdr.detectChanges();

				this.readFile(files[index], reader, (event) => {
					var image = new Image();
					this.readImage(event, image, (imgresult) => {
						var canvas = document.createElement("canvas");
						var context = canvas.getContext("2d");

						var ratio = Math.min(max_width / imgresult.width, max_height / imgresult.height);
						if (imgresult.width < imgresult.width * ratio && imgresult.height < imgresult.height * ratio) {
							canvas.width = imgresult.width;
							canvas.height = imgresult.height;
						} else {
							canvas.width = imgresult.width * ratio;
							canvas.height = imgresult.height * ratio;
						}
						context.drawImage(image,
							0,
							0,
							image.width,
							image.height,
							0,
							0,
							canvas.width,
							canvas.height
						);

						if (canvas.width <= max_width && canvas.height <= max_height) {
							const dataUrl = canvas.toDataURL("image/" + this.imageExtension, 1);
							currentFile['originalFile'] = this.dataURLtoFile(dataUrl, currentFile.text + '.' + this.imageExtension);
							// Force cropper to reload by clearing and incrementing key
							this.imageUrlPath = undefined;
							this.imageKey = -1; // Hide cropper temporarily
							this.cdr.detectChanges();
							// Use setTimeout to ensure DOM updates before showing new image
							setTimeout(() => {
								this.imageKey++;
								this.imageUrlPath = dataUrl;
								this.imageSelected = currentFile;
								this.pictureLink = this.imageUrlPath;
								this.loading = false;
								this.spinnerService.hide();
								this.cdr.detectChanges();
							}, 10);
						} else {
							this.layoutUtilsService.showNotification(this.translate.instant('The image is not well formatted'), this.translate.instant('Dismiss'));
							this.loading = false;
							this.spinnerService.hide();
							this.cdr.detectChanges();
						}
					});
				});
			}
		} else {
			this.cdr.detectChanges();
		}
	}
	dataURLtoFile(dataurl, filename) {
		var arr = dataurl.split(','),
			mime = arr[0].match(/:(.*?);/)[1],
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], filename, { type: mime });
	}
	readFile(file, reader, callback): void {
		reader.onload = () => {
			callback(reader.result);
		}
		reader.readAsDataURL(file);
	}
	readImage(file, image, callback): void {
		image.onload = () => {
			callback(image);
		}
		image.src = file;
	}
	continueUpload(currentFile, base64Image) {
		this.closeModal({ currentFile: currentFile, base64Image: base64Image });
		// if (!this.loading && currentFile && currentFile.originalFile) {
		// 	this.loading = true;
		// 	this.errorMessage = '';
		// 	// this.requestService.onUploadUserImage(currentFile, this.currentselectedUser._id)
		// 	// 	.subscribe(
		// 	// 		(results: any) => {
		// 	// 			// console.log('currentFile', currentFile);
		// 	// 			if (results['status']) {
		// 	// 				currentFile.source_url = results.results.pictureLink;
		// 	// 				this.pictureLink = results.results.pictureLink;
		// 	// 				this.layoutUtilsService.showNotification(this.translate.instant('Profile image uploaded successfully'), this.translate.instant('Dismiss'));
		// 	// 				if (this.data.profile) {
		// 	// 					this.requestService.updateUserData('pictureLink', results.results.pictureLink); // update selecteduser image
		// 	// 				}
		// 	// 				this.loading = false;
		// 	// 				this.closeModal(results.results.pictureLink);
		// 	// 			} else {
		// 	// 				this.loading = false;
		// 	// 				currentFile.error = true;
		// 	// 				this.layoutUtilsService.showNotification('Error: ' + results['message'], 'Dismiss');
		// 	// 			}
		// 	// 			this.cdr.detectChanges();
		// 	// 		},
		// 	// 		(error: any) => {
		// 	// 			// console.log('Error uploading file.', error);
		// 	// 			this.loading = false;
		// 	// 			currentFile.error = true;
		// 	// 			this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + ' ' + this.translate.instant('Error uploading file'), this.translate.instant('Dismiss'));
		// 	// 			this.cdr.detectChanges();
		// 	// 		}
		// 	// 	);
		// } else {
		// 	this.closeModal(undefined);
		// }
	}
	// fileChangeEvent(event: any): void {
	//     this.imageChangedEvent = event;
	// }
	cropIt() {
		let croppedImage = this.imageCropper.crop();
		if (!this.loading && this.imageSelected && croppedImage) {
			this.imageSelected['originalFile'] = this.dataURLtoFile(croppedImage.base64, this.imageSelected.text + '.' + this.imageExtension);
			this.continueUpload(this.imageSelected, croppedImage.base64);
		}
	}
	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64;
	}
	imageHasLoaded(e) {
		//  console.log('imageHasLoaded', e);
	}
	imageLoaded() {
		// show cropper
		this.cdr.detectChanges();
	}
	cropperReady() {
		// cropper ready
		this.cdr.detectChanges();
	}
	loadImageFailed() {
		// show message
		this.layoutUtilsService.showNotification(
			this.translate.instant('Error: ') + ' ' + this.translate.instant('Failed to load image'),
			this.translate.instant('Dismiss')
		);
		this.imageUrlPath = undefined;
		this.loading = false;
		this.cdr.detectChanges();
	}
	public clearIt(e) {
		// if (e) {
		// 	e.stopImmediatePropagation();
		// 	e.preventDefault();
		// 	// e.stopPropagation();
		// }
		if (!this.loading) {
			const _title: string = this.dataTypeDisplay + ' ' + this.translate.instant('Deletion');
			const _description: string = this.translate.instant('Are you sure you want to permanently delete this') + ' ' + this.dataTypeDisplay + '?';
			const _waitDesciption: string = this.translate.instant('Deleting') + '...';

			const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
			dialogRef.afterClosed().subscribe(res => {
				if (!res) {
					return;
				}
				this.realDelete();
			});
		}
	}
	public realDelete() {
		const _deleteMessage = this.dataTypeDisplay + ' ' + this.translate.instant('Deleted Successfully') + '.';
		// this.layoutUtilsService.showNotification(_deleteMessage,  this.translate.instant('Dismiss'));
		if (!this.loadingDelete) {
			this.loadingDelete = true;
			this.errorMessage = '';

			let url = 'client/image/profile/' + this.data.clientId + '/delete';
			if (this.data.isUser) {
				url = 'user/' + this.data.clientId + '/deleteprofile';
			}

			this.requestService.postRequest({}, url, (data, error) => {
				if (error) {
					this.errorMessage = error;
					// this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
				}
				if (data) {
					this.profileImageDeleted = true;
					this.layoutUtilsService.showNotification(_deleteMessage, this.translate.instant('Dismiss'));
					if (this.data.image && this.currentselectedUser?._id == this.data.clientId) {
						this.requestService.updateUserData('pictureLink', this.defaultPictureLink);
					}
				}

				// Keep state updates in one place to avoid NG0100 from rapid true/false flips.
				this.originalImage = this.defaultPictureLink;
				this.pictureLink = this.defaultPictureLink;
				this.imageUrlPath = undefined;
				this.imageKey = 0;
				this.imageSelected = {
					error: false,
					text: this.getFilename(this.defaultPictureLink),
					originalFile: undefined,
					source_url: null
				};
				setTimeout(() => {
					this.loadingDelete = false;
					this.cdr.detectChanges();
				}, 300);
				this.cdr.detectChanges();
				if (this.imageInput?.nativeElement) {
					this.imageInput.nativeElement.value = '';
				}
			});
		}
	}
	getBase64ImageFromURL(url: string) {
		return Observable.create((observer: Observer<string>) => {
			// create an image object
			let img = new Image();
			img.crossOrigin = 'Anonymous';
			img.src = url + '?t=' + new Date().getTime();
			if (!img.complete) {
				// This will call another method that will create image from url
				img.onload = () => {
					observer.next(this.getBase64Image(img));
					observer.complete();
				};
				img.onerror = (err) => {
					observer.error(err);
				};
			} else {
				observer.next(this.getBase64Image(img));
				observer.complete();
			}
		});
	}
	getBase64Image(img: HTMLImageElement) {
		// We create a HTML canvas object that will create a 2d image
		var canvas = document.createElement("canvas");
		canvas.width = img.width;
		canvas.height = img.height;
		var ctx = canvas.getContext("2d");
		// This will draw image
		ctx.drawImage(img, 0, 0);
		// Convert the drawn image to Data URL
		var dataURL = canvas.toDataURL("image/" + this.imageExtension, 1);
		return dataURL.replace(/^data:image\/(png|jpg|jpeg|gif);base64,/, "");
	}
}
